import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ResponseAPI } from '../models/HttpBase/response-api';
import {
  PistaEstadoActivo,
  PistaEstadoFetchResult,
  PistaLiveIdentityContext,
  PistaLiveScope
} from '../models/moderacion/pista-live.dto';
import {
  buildScopeIdentity,
  normalizeIdentityText,
  toPositiveInt
} from './pista-live-identity.util';
import { HttpClientService } from './http-client.service';

type RawRecord = Record<string, unknown>;

@Injectable({
  providedIn: 'root'
})
export class PistaLiveDataService {
  // EN CURSO / EN PISTA must always come from the public endpoint
  // so both guest and authenticated users see the same live state.
  private readonly publicReadPath = 'pista/public/list';

  constructor(private readonly httpClientService: HttpClientService) {}

  public listByExposicion(exposicionId: number): Observable<PistaEstadoFetchResult> {
    const expo = toPositiveInt(exposicionId);
    if (!expo) {
      return of(
        this.buildResult({
          exposicionId,
          source: 'public',
          hasError: true,
          forbidden: false,
          message: 'Exposicion invalida.',
          estados: []
        })
      );
    }

    return this.fetchFromPath(`${this.publicReadPath}/${expo}`, expo, 'public');
  }

  private fetchFromPath(
    path: string,
    exposicionId: number,
    source: 'moderacion' | 'public'
  ): Observable<PistaEstadoFetchResult> {
    return this.httpClientService.getSilent<unknown>(path).pipe(
      map((rawResponse) => this.hydrateResponse<unknown>(rawResponse)),
      map((response) => this.mapResponse(path, response, exposicionId, source)),
      catchError((error: unknown) =>
        of(
          this.buildResult({
            exposicionId,
            source,
            hasError: true,
            forbidden: this.isAccessDeniedStatus(this.resolveErrorStatus(error)),
            message: this.resolveErrorMessage(error, `No se pudo leer estado en ${path}.`),
            estados: []
          })
        )
      )
    );
  }

  private mapResponse(
    path: string,
    response: ResponseAPI<unknown>,
    exposicionId: number,
    source: 'moderacion' | 'public'
  ): PistaEstadoFetchResult {
    if (!response.Success) {
      return this.buildResult({
        exposicionId,
        source,
        hasError: true,
        forbidden: this.isAccessDeniedStatus(response.ResponseStatus),
        message: normalizeIdentityText(response.ResponseDescription) || `Respuesta invalida en ${path}.`,
        estados: []
      });
    }

    const rawItems = this.extractItems(response.ResponseObject);
    const normalized = rawItems
      .map((item) => this.normalizeItem(item, exposicionId))
      .filter((item): item is PistaEstadoActivo => !!item)
      .sort((a, b) => a.identity.localeCompare(b.identity));

    return this.buildResult({
      exposicionId,
      source,
      hasError: false,
      forbidden: false,
      message: '',
      estados: normalized
    });
  }

  private normalizeItem(raw: RawRecord, fallbackExposicionId: number): PistaEstadoActivo | null {
    const scope = this.normalizeScope(raw);
    if (!scope) {
      return null;
    }

    const activo = this.toBoolean(this.readRaw(raw, ['Activo', 'activo']));
    if (!activo) {
      return null;
    }

    const exposicionId =
      toPositiveInt(this.readRaw(raw, ['ExposicionID', 'exposicionId'])) ?? fallbackExposicionId;
    if (!toPositiveInt(exposicionId)) {
      return null;
    }

    const identityContext: PistaLiveIdentityContext = {
      exposicionId,
      especialId: this.toNullableText(this.readRaw(raw, ['EspecialID', 'especialId'])),
      inscripcionOrden: toPositiveInt(
        this.readRaw(raw, ['InscripcionOrden', 'inscripcionOrden', 'OrdenInscripcion', 'ordenInscripcion'])
      ),
      inscripcionSexo: this.toNullableText(
        this.readRaw(raw, ['InscripcionSexo', 'inscripcionSexo', 'SexoInscripcion', 'sexoInscripcion'])
      ),
      inscripcionTipo: this.toNullableText(
        this.readRaw(raw, ['InscripcionTipo', 'inscripcionTipo', 'TipoInscripcion', 'tipoInscripcion'])
      ),
      campeonatoOrden: toPositiveInt(
        this.readRaw(raw, ['CampeonatoOrden', 'campeonatoOrden', 'OrdenCampeonato', 'ordenCampeonato'])
      ),
      campeonatoNombre: this.toNullableText(
        this.readRaw(raw, ['CampeonatoNombre', 'campeonatoNombre', 'NombreCampeonato', 'nombreCampeonato'])
      ),
      categoriaOrden: toPositiveInt(
        this.readRaw(raw, ['CategoriaOrden', 'categoriaOrden', 'OrdenCategoria', 'ordenCategoria'])
      ),
      categoriaNombre: this.toNullableText(
        this.readRaw(raw, [
          'CategoriaNombre',
          'categoriaNombre',
          'NombreCategoria',
          'nombreCategoria',
          'Categoria',
          'categoria'
        ])
      ),
      productoId: toPositiveInt(this.readRaw(raw, ['ProductoID', 'productoId', 'IdProducto', 'idProducto']))
    };

    const identity = buildScopeIdentity(scope, identityContext);
    if (!identity) {
      return null;
    }

    return {
      exposicionId,
      scope,
      identity,
      activo: true,
      targetLabel: this.toNullableText(this.readRaw(raw, ['TargetLabel', 'targetLabel'])) ?? '',
      especialId: identityContext.especialId ?? null,
      inscripcionOrden: identityContext.inscripcionOrden ?? null,
      inscripcionSexo: identityContext.inscripcionSexo ?? null,
      inscripcionTipo: identityContext.inscripcionTipo ?? null,
      campeonatoOrden: identityContext.campeonatoOrden ?? null,
      campeonatoNombre: identityContext.campeonatoNombre ?? null,
      categoriaOrden: identityContext.categoriaOrden ?? null,
      categoriaNombre: identityContext.categoriaNombre ?? null,
      productoId: identityContext.productoId ?? null,
      rp: this.toNullableText(this.readRaw(raw, ['RP', 'rp'])),
      updatedUtc: this.toNullableText(this.readRaw(raw, ['UpdatedUtc', 'updatedUtc', 'ActualizadoUtc', 'actualizadoUtc'])),
      updatedBy: this.toNullableText(this.readRaw(raw, ['UpdatedBy', 'updatedBy', 'ActualizadoPor', 'actualizadoPor']))
    };
  }

  private readRaw(raw: RawRecord, keys: string[]): unknown {
    for (const key of keys) {
      if (Object.prototype.hasOwnProperty.call(raw, key)) {
        return raw[key];
      }
    }
    return null;
  }

  private extractItems(source: unknown): RawRecord[] {
    if (Array.isArray(source)) {
      return source.filter((item): item is RawRecord => !!item && typeof item === 'object');
    }

    if (!source || typeof source !== 'object') {
      return [];
    }

    const value = source as Record<string, unknown>;
    const candidates = [
      value['items'],
      value['Items'],
      value['estados'],
      value['Estados'],
      value['ResponseObject']
    ];

    for (const candidate of candidates) {
      if (Array.isArray(candidate)) {
        return candidate.filter((item): item is RawRecord => !!item && typeof item === 'object');
      }
    }

    return [];
  }

  private buildResult(params: {
    exposicionId: number;
    source: 'moderacion' | 'public';
    hasError: boolean;
    forbidden: boolean;
    message: string;
    estados: PistaEstadoActivo[];
  }): PistaEstadoFetchResult {
    const hash = this.buildHash(params.estados, params.hasError, params.forbidden, params.message);
    return {
      exposicionId: params.exposicionId,
      estados: params.estados,
      hash,
      hasError: params.hasError,
      forbidden: params.forbidden,
      message: params.message,
      source: params.source
    };
  }

  private buildHash(
    estados: PistaEstadoActivo[],
    hasError: boolean,
    forbidden: boolean,
    message: string
  ): string {
    const base = estados
      .map((item) => `${item.scope}|${item.identity}|${item.updatedUtc ?? ''}|${item.targetLabel}`)
      .join('||');

    return `${hasError ? '1' : '0'}|${forbidden ? '1' : '0'}|${message}|${base}`;
  }

  private toNullableText(value: unknown): string | null {
    const normalized = normalizeIdentityText(value);
    return normalized || null;
  }

  private toBoolean(value: unknown): boolean {
    if (typeof value === 'boolean') {
      return value;
    }
    if (typeof value === 'number') {
      return value > 0;
    }
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      return normalized === 'true' || normalized === '1' || normalized === 'si' || normalized === 'yes';
    }
    return false;
  }

  private normalizeScope(raw: RawRecord): PistaLiveScope | null {
    const value = this.toNullableText(this.readRaw(raw, ['Scope', 'scope']));
    if (!value) {
      return null;
    }

    const normalized = value.toLowerCase();
    if (normalized === 'category') {
      return 'categoria';
    }
    if (normalized === 'championship') {
      return 'campeonato';
    }
    if (normalized === 'inscription') {
      return 'inscripcion';
    }
    if (normalized === 'special') {
      return 'especial';
    }
    if (normalized === 'product') {
      return 'producto';
    }
    if (
      normalized === 'especial' ||
      normalized === 'inscripcion' ||
      normalized === 'campeonato' ||
      normalized === 'categoria' ||
      normalized === 'producto'
    ) {
      return normalized;
    }
    return null;
  }

  private resolveErrorStatus(error: unknown): number {
    if (!error || typeof error !== 'object') {
      return 0;
    }

    const value = error as Record<string, unknown>;
    const direct = Number(value['status']);
    if (Number.isFinite(direct) && direct > 0) {
      return Math.trunc(direct);
    }

    const nestedError = value['error'];
    if (nestedError && typeof nestedError === 'object') {
      const nestedStatus = Number((nestedError as Record<string, unknown>)['status']);
      if (Number.isFinite(nestedStatus) && nestedStatus > 0) {
        return Math.trunc(nestedStatus);
      }
    }

    return 0;
  }

  private isAccessDeniedStatus(status: unknown): boolean {
    const numeric = Number(status);
    if (!Number.isFinite(numeric)) {
      return false;
    }

    const normalized = Math.trunc(numeric);
    return normalized === 401 || normalized === 403;
  }

  private resolveErrorMessage(error: unknown, fallback: string): string {
    if (!error || typeof error !== 'object') {
      return fallback;
    }

    const source = error as Record<string, unknown>;
    const candidates = [
      source['ResponseDescription'],
      source['responseDescription'],
      source['message'],
      source['Message']
    ];

    for (const candidate of candidates) {
      const normalized = this.toNullableText(candidate);
      if (normalized) {
        return normalized;
      }
    }

    const nested = source['error'];
    if (nested && typeof nested === 'object') {
      const nestedSource = nested as Record<string, unknown>;
      const nestedCandidates = [
        nestedSource['ResponseDescription'],
        nestedSource['responseDescription'],
        nestedSource['message'],
        nestedSource['Message']
      ];

      for (const candidate of nestedCandidates) {
        const normalized = this.toNullableText(candidate);
        if (normalized) {
          return normalized;
        }
      }
    }

    return fallback;
  }

  private hydrateResponse<T>(response: unknown): ResponseAPI<T> {
    return Object.assign(new ResponseAPI<T>(), response);
  }
}
